export type DraftFieldValue = string | string[] | boolean;

export interface DraftFileMeta {
  fieldName: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  lastModified: number;
}

export interface FormDraftSnapshot {
  schemaVersion: 1;
  formKey: string;
  draftId: string;
  createdAt: string;
  updatedAt: string;
  lastSectionId: string;
  fields: Record<string, DraftFieldValue>;
  fileMeta: DraftFileMeta[];
  filesStored: boolean;
}

interface StoredDraftFile extends DraftFileMeta {
  id: string;
  formKey: string;
  draftId: string;
  file: File;
}

const STORAGE_PREFIX = "kreatethedate:form-progress";
const DB_NAME = "kreatethedate-form-progress";
const FILE_STORE = "draft-files";

export function createDraftId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getResumeDraftId() {
  return new URLSearchParams(window.location.search).get("resume") || "";
}

export function buildResumeUrl(pathname: string, draftId: string, sectionId: string) {
  const url = new URL(pathname, window.location.origin);
  url.searchParams.set("resume", draftId);
  if (sectionId) url.hash = sectionId;
  return url.toString();
}

export function getCurrentSectionId(sectionIds: string[]) {
  const anchorOffset = 120;
  let bestId = sectionIds[0] || "";
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const sectionId of sectionIds) {
    const section = document.getElementById(sectionId);
    if (!section) continue;
    const distance = Math.abs(section.getBoundingClientRect().top - anchorOffset);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestId = sectionId;
    }
  }

  return bestId;
}

export async function saveFormProgress(options: {
  form: HTMLFormElement;
  formKey: string;
  draftId?: string;
  lastSectionId: string;
}) {
  const draftId = options.draftId || createDraftId();
  const existing = loadFormProgress(options.formKey, draftId);
  const fileSave = await replaceDraftFiles(options.formKey, draftId, options.form);
  const now = new Date().toISOString();
  const draft: FormDraftSnapshot = {
    schemaVersion: 1,
    formKey: options.formKey,
    draftId,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    lastSectionId: options.lastSectionId,
    fields: collectFormFields(options.form),
    fileMeta: fileSave.fileMeta,
    filesStored: fileSave.filesStored,
  };

  localStorage.setItem(storageKey(options.formKey, draftId), JSON.stringify(draft));
  return draft;
}

export function loadFormProgress(formKey: string, draftId: string) {
  const raw = localStorage.getItem(storageKey(formKey, draftId));
  if (!raw) return null;

  try {
    const draft = JSON.parse(raw) as FormDraftSnapshot;
    return draft.formKey === formKey && draft.draftId === draftId ? draft : null;
  } catch {
    return null;
  }
}

export function restoreFormProgress(form: HTMLFormElement, draft: FormDraftSnapshot) {
  for (const [fieldName, value] of Object.entries(draft.fields)) {
    const controls = form.elements.namedItem(fieldName);
    if (!controls) continue;
    const controlList = controls instanceof RadioNodeList ? Array.from(controls) : [controls];

    for (const control of controlList) {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement || control instanceof HTMLSelectElement)) continue;

      if (control instanceof HTMLInputElement && control.type === "checkbox") {
        control.checked = Array.isArray(value) ? value.includes(control.value) : Boolean(value);
      } else if (control instanceof HTMLInputElement && control.type === "radio") {
        control.checked = value === control.value;
      } else if (control instanceof HTMLSelectElement && control.multiple && Array.isArray(value)) {
        Array.from(control.options).forEach((option) => {
          option.selected = value.includes(option.value);
        });
      } else if (typeof value === "string") {
        control.value = value;
      }

      control.dispatchEvent(new Event("input", { bubbles: true }));
      control.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }
}

export async function appendStoredDraftFiles(formData: FormData, formKey: string, draftId: string) {
  const records = await getStoredDraftFiles(formKey, draftId);

  for (const record of records) {
    const hasCurrentFile = formData
      .getAll(record.fieldName)
      .some((entry) => entry instanceof File && entry.size > 0);
    if (!hasCurrentFile) formData.append(record.fieldName, record.file, record.fileName);
  }
}

function collectFormFields(form: HTMLFormElement) {
  const fields: Record<string, DraftFieldValue> = {};

  Array.from(form.elements).forEach((element) => {
    if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement)) return;
    if (!element.name || element.disabled) return;
    if (["button", "submit", "reset", "file", "hidden"].includes(element.type)) return;

    if (element instanceof HTMLInputElement && element.type === "checkbox") {
      const namedControls = form.elements.namedItem(element.name);
      const controlList = namedControls instanceof RadioNodeList ? Array.from(namedControls) : [namedControls];
      const checkboxes = controlList.filter(
        (control): control is HTMLInputElement => control instanceof HTMLInputElement && control.type === "checkbox",
      );

      fields[element.name] =
        checkboxes.length > 1 ? checkboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value) : element.checked;
    } else if (element instanceof HTMLInputElement && element.type === "radio") {
      if (element.checked) fields[element.name] = element.value;
    } else if (element instanceof HTMLSelectElement && element.multiple) {
      fields[element.name] = Array.from(element.selectedOptions).map((option) => option.value);
    } else {
      fields[element.name] = element.value;
    }
  });

  return fields;
}

function collectSelectedFileMeta(form: HTMLFormElement) {
  const fileMeta: DraftFileMeta[] = [];

  Array.from(form.elements).forEach((element) => {
    if (!(element instanceof HTMLInputElement) || element.type !== "file" || !element.name || !element.files) return;
    Array.from(element.files).forEach((file) => {
      if (!file.size) return;
      fileMeta.push({
        fieldName: element.name,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        lastModified: file.lastModified,
      });
    });
  });

  return fileMeta;
}

async function replaceDraftFiles(formKey: string, draftId: string, form: HTMLFormElement) {
  const fileMeta = collectSelectedFileMeta(form);
  if (!fileMeta.length) {
    await clearDraftFiles(formKey, draftId).catch(() => undefined);
    return { fileMeta, filesStored: true };
  }

  try {
    await clearDraftFiles(formKey, draftId);
    const db = await openDraftDb();
    const tx = db.transaction(FILE_STORE, "readwrite");
    const store = tx.objectStore(FILE_STORE);

    Array.from(form.elements).forEach((element) => {
      if (!(element instanceof HTMLInputElement) || element.type !== "file" || !element.name || !element.files) return;
      Array.from(element.files).forEach((file) => {
        if (!file.size) return;
        const record: StoredDraftFile = {
          id: `${formKey}:${draftId}:${element.name}:${createDraftId()}`,
          formKey,
          draftId,
          fieldName: element.name,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          lastModified: file.lastModified,
          file,
        };
        store.put(record);
      });
    });

    await transactionDone(tx);
    db.close();
    return { fileMeta, filesStored: true };
  } catch {
    return { fileMeta, filesStored: false };
  }
}

async function getStoredDraftFiles(formKey: string, draftId: string) {
  try {
    const db = await openDraftDb();
    const tx = db.transaction(FILE_STORE, "readonly");
    const index = tx.objectStore(FILE_STORE).index("byDraft");
    const request = index.getAll([formKey, draftId]);
    const records = await requestResult<StoredDraftFile[]>(request);
    db.close();
    return records;
  } catch {
    return [];
  }
}

async function clearDraftFiles(formKey: string, draftId: string) {
  const db = await openDraftDb();
  const tx = db.transaction(FILE_STORE, "readwrite");
  const index = tx.objectStore(FILE_STORE).index("byDraft");
  const request = index.openCursor([formKey, draftId]);

  await new Promise<void>((resolve, reject) => {
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve();
        return;
      }
      cursor.delete();
      cursor.continue();
    };
    request.onerror = () => reject(request.error);
  });

  await transactionDone(tx);
  db.close();
}

function storageKey(formKey: string, draftId: string) {
  return `${STORAGE_PREFIX}:${formKey}:${draftId}`;
}

function openDraftDb() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available."));
      return;
    }

    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(FILE_STORE)) {
        const store = db.createObjectStore(FILE_STORE, { keyPath: "id" });
        store.createIndex("byDraft", ["formKey", "draftId"], { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function requestResult<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(tx: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}
